// ============================================================================
// LT-4a — THE ONBOARDING WIZARD. Three questions, one per screen. Two if they
// are not VAT registered.
//
// ⛔ ONE SAVE, AT FINISH, WITH THE WHOLE PAYLOAD. NEVER PER STEP.
// The reasoning is in useOnboarding.js and it is the trap in this package:
// saveBusinessSettings() upserts, so a per-step save would create the row at
// question 1 and permanently mark an abandoned wizard as complete.
//
// ⛔ THE WORD "BASIS" NEVER APPEARS ON SCREEN. Question 3 asks WHEN they account
// for VAT and maps the answer to 'invoice' | 'cash'. A sole trader does not know
// what a VAT basis is, and being asked is how they conclude this software is for
// somebody else.
//
// FAIL OPEN: a save error shows the error, offers a retry, and offers a way into
// the app that records NOTHING — so the wizard is still there next time.
// ============================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { C } from "../customers/theme";

// ── VAT number: WARN, NEVER BLOCK ───────────────────────────────────────────
// Irish VAT numbers are IE + 7 digits + 1-2 letters, but this field also has to
// accept a UK, NI or other EU registration from a business trading across the
// border — and Revenue's own formats have changed over time.
//
// ⛔ A HARD REJECT HERE TURNS A FORMAT OPINION INTO A LOCKED DOOR at the first
// screen a paying customer ever sees, and the person who knows their own VAT
// number is right is the customer, not this regex. So it warns and moves on. The
// database has no CHECK on vat_number either, which is the same judgement.
const VAT_LOOSE = /^[A-Z]{2}[0-9A-Z]{6,12}$/i;
export function vatLooksOdd(raw) {
  const v = String(raw ?? "").replace(/[\s-]/g, "");
  if (!v) return false;                 // empty is not "odd" — it is just empty
  return !VAT_LOOSE.test(v);
}

const STEP_NAME = 1;
const STEP_VAT = 2;
const STEP_TIMING = 3;
const STEP_DONE = 4;

export default function OnboardingWizard({ companyName, onComplete, onSkip, onDismiss }) {
  const [step, setStep] = useState(STEP_NAME);
  const [name, setName] = useState(companyName || "");
  const [vatRegistered, setVatRegistered] = useState(null);  // null | true | false
  const [vatNumber, setVatNumber] = useState("");
  const [timing, setTiming] = useState("invoice");           // the DEFAULT, stated in the brief
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => { if (step === STEP_NAME && nameRef.current) nameRef.current.focus(); }, [step]);

  const canContinueName = name.trim().length > 0;

  const finish = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      // ── THE WHOLE PAYLOAD, BUILT ONCE, SENT ONCE ──────────────────────────
      // trading_name is the ONLY name written. A definer trigger on
      // business_settings (20260803120000_company_name_sync) fires AFTER INSERT
      // OR UPDATE OF legal_name, trading_name and sets companies.name from
      // coalesce(nullif(btrim(trading_name),''), nullif(btrim(legal_name),'')).
      // So this one field updates the company name everywhere in the product,
      // and there is deliberately no second write to companies to keep in step.
      const payload = { trading_name: name.trim() };

      if (vatRegistered) {
        payload.vat_number = vatNumber.trim();
        // ⚠️ vat_basis IS ONLY SENT WHEN THEY ARE VAT REGISTERED. For a business
        // that is not, there is no question 3 and no answer to record — sending
        // a default would be inventing an answer to a question we never asked.
        // The column is `not null default 'invoice'`, so omitting it is exactly
        // right: the database's own default applies and nothing is fabricated.
        payload.vat_basis = timing;
      }

      await onComplete(payload);
      setStep(STEP_DONE);
    } catch (e) {
      // FAIL OPEN. Stay on screen, show what happened, offer a retry — and offer
      // a door into the app that records NOTHING, so the wizard returns.
      setError(e?.message || "We could not save that. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [name, vatRegistered, vatNumber, timing, onComplete]);

  // ── layout ────────────────────────────────────────────────────────────────
  // Full-screen, one question per viewport, primary action fixed at the bottom
  // in thumb reach. `100dvh` (with a vh fallback) because on mobile Safari 100vh
  // is taller than the visible area, which would push the primary button under
  // the browser chrome — the one control that must never be unreachable.
  const overlay = {
    position: "fixed", inset: 0, zIndex: 400, background: C.bg,
    display: "flex", flexDirection: "column",
    height: "100vh", maxHeight: "100dvh",
    fontFamily: "'Inter', system-ui, sans-serif", color: C.text,
  };
  const body = {
    flex: 1, overflowY: "auto", padding: "32px 20px 16px",
    display: "flex", flexDirection: "column", alignItems: "center",
  };
  const inner = { width: "100%", maxWidth: 460 };
  const footer = {
    padding: "12px 20px calc(16px + env(safe-area-inset-bottom, 0px))",
    borderTop: `1px solid ${C.borderSoft}`, background: C.bg,
  };
  const question = { fontSize: 23, fontWeight: 700, lineHeight: 1.3, marginBottom: 8 };
  const helper = { fontSize: 13.5, color: C.textMuted, lineHeight: 1.55, marginBottom: 24 };
  // 44px minimum touch target on every interactive element below — the iOS
  // guideline, and the practical floor for a thumb on a moving bus.
  const primary = {
    width: "100%", minHeight: 48, padding: "13px 18px", borderRadius: 10, border: "none",
    background: C.primary, color: C.bg, fontSize: 15.5, fontWeight: 700, cursor: "pointer",
  };
  const quiet = {
    width: "100%", minHeight: 44, marginTop: 10, padding: "11px 18px", borderRadius: 10,
    border: "none", background: "transparent", color: C.textMuted, fontSize: 13.5,
    fontWeight: 600, cursor: "pointer",
  };
  const bigChoice = (selected) => ({
    width: "100%", minHeight: 56, marginBottom: 12, padding: "16px 18px", borderRadius: 12,
    border: `1.5px solid ${selected ? C.primary : C.border}`,
    background: selected ? C.primaryDim : C.surface,
    color: C.text, fontSize: 16, fontWeight: 700, cursor: "pointer",
    textAlign: "left", display: "block",
  });
  const card = (selected) => ({
    width: "100%", minHeight: 76, marginBottom: 12, padding: "16px 18px", borderRadius: 12,
    border: `1.5px solid ${selected ? C.primary : C.border}`,
    background: selected ? C.primaryDim : C.surface,
    color: C.text, cursor: "pointer", textAlign: "left", display: "block",
  });
  const input = {
    width: "100%", minHeight: 48, padding: "13px 14px", borderRadius: 10,
    border: `1px solid ${C.border}`, background: C.surface2, color: C.text,
    fontSize: 16, boxSizing: "border-box", outline: "none",
  };

  // Total steps shown in the progress line: 3 while VAT registration is unknown
  // or true, 2 once they say no. It counts DOWN honestly rather than promising
  // three screens and delivering two.
  const totalSteps = vatRegistered === false ? 2 : 3;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Set up your workspace">
      <div style={body}>
        <div style={inner}>
          {step !== STEP_DONE && (
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: 0.6, marginBottom: 18 }}>
              STEP {step} OF {totalSteps}
            </div>
          )}

          {/* ── 1 ── */}
          {step === STEP_NAME && (
            <>
              <h1 style={question}>What's your business called?</h1>
              <p style={helper}>This is the name your customers will see on invoices.</p>
              {/* aria-label rather than a visually-hidden <label> duplicating the
                  <h1>: two nodes with identical text make every findByText in the
                  test suite ambiguous, and a screen reader read the question twice. */}
              <input
                id="ob-name" ref={nameRef} style={input} value={name}
                aria-label="Your business name"
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Trading Ltd" autoComplete="organization"
                onKeyDown={(e) => { if (e.key === "Enter" && canContinueName) setStep(STEP_VAT); }}
              />
            </>
          )}

          {/* ── 2 ── */}
          {step === STEP_VAT && (
            <>
              <h1 style={question}>Are you registered for VAT?</h1>
              <p style={helper}>If you're not sure, you're probably not — most new businesses aren't until they pass the turnover threshold.</p>
              <button type="button" style={bigChoice(vatRegistered === true)} onClick={() => setVatRegistered(true)}>
                Yes, I'm VAT registered
              </button>
              <button type="button" style={bigChoice(vatRegistered === false)} onClick={() => setVatRegistered(false)}>
                No, not registered
              </button>
              {vatRegistered === true && (
                <div style={{ marginTop: 16 }}>
                  <label htmlFor="ob-vat" style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: C.textMuted, marginBottom: 7 }}>
                    Your VAT number
                  </label>
                  <input
                    id="ob-vat" style={input} value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    placeholder="IE1234567T" autoCapitalize="characters" spellCheck={false}
                  />
                  {/* WARN, NEVER BLOCK — Continue stays enabled underneath this. */}
                  {vatLooksOdd(vatNumber) && (
                    <p role="status" style={{ marginTop: 8, fontSize: 12.5, color: C.warning, lineHeight: 1.5 }}>
                      That doesn't look like a typical VAT number — worth a second look. You can carry on either way.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── 3, VAT ONLY ── */}
          {step === STEP_TIMING && (
            <>
              <h1 style={question}>When do you account for VAT?</h1>
              <p style={helper}>Most businesses use the first one. You can change this later in Business Settings.</p>
              <button type="button" style={card(timing === "invoice")} onClick={() => setTiming("invoice")} aria-pressed={timing === "invoice"}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 5 }}>When I send the invoice</div>
                <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.5, fontWeight: 400 }}>
                  You owe the VAT as soon as you invoice, even if you haven't been paid yet.
                </div>
              </button>
              <button type="button" style={card(timing === "cash")} onClick={() => setTiming("cash")} aria-pressed={timing === "cash"}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 5 }}>When I get paid</div>
                <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.5, fontWeight: 400 }}>
                  You only owe the VAT once the money actually reaches you.
                </div>
              </button>
            </>
          )}

          {/* ── FINISH ── */}
          {step === STEP_DONE && (
            <>
              <div style={{ fontSize: 40, marginBottom: 12 }} aria-hidden="true">🎉</div>
              <h1 style={question}>You're set up.</h1>
              <p style={helper}>That's everything we needed. You can change any of it in Business Settings whenever you like.</p>
            </>
          )}

          {error && (
            <div role="alert" style={{ marginTop: 18, background: C.dangerDim, border: `1px solid ${C.danger}`, borderRadius: 10, padding: "11px 14px", fontSize: 13, lineHeight: 1.5 }}>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Primary action FIXED AT THE BOTTOM, in thumb reach, outside the
          scrolling area so it cannot be scrolled away from. */}
      <div style={footer}>
        <div style={{ ...inner, margin: "0 auto" }}>
          {step === STEP_NAME && (
            <>
              <button type="button" style={{ ...primary, opacity: canContinueName ? 1 : 0.45 }} disabled={!canContinueName} onClick={() => setStep(STEP_VAT)}>
                Continue
              </button>
              <button type="button" style={quiet} onClick={onSkip}>I'll do this later</button>
            </>
          )}

          {step === STEP_VAT && (
            <>
              <button
                type="button"
                style={{ ...primary, opacity: vatRegistered === null ? 0.45 : 1 }}
                disabled={vatRegistered === null || busy}
                onClick={() => {
                  // ⛔ THE ONLY PLACE QUESTION 3 IS DECIDED. "No" does not skip
                  // past step 3 — step 3 never exists for them, and `finish()`
                  // omits vat_basis entirely.
                  if (vatRegistered) setStep(STEP_TIMING);
                  else finish();
                }}
              >
                {busy ? "Saving…" : vatRegistered === false ? "Finish" : "Continue"}
              </button>
              <button type="button" style={quiet} onClick={() => setStep(STEP_NAME)}>Back</button>
            </>
          )}

          {step === STEP_TIMING && (
            <>
              <button type="button" style={primary} disabled={busy} onClick={finish}>
                {busy ? "Saving…" : "Finish"}
              </button>
              <button type="button" style={quiet} onClick={() => setStep(STEP_VAT)}>Back</button>
            </>
          )}

          {step === STEP_DONE && (
            <>
              {/* LT-4b hooks here. A no-op today, and it CLOSES the wizard rather
                  than doing nothing visible — a button that appears inert is worse
                  than no button. */}
              <button type="button" style={primary} onClick={onDismiss}>Show me around</button>
              <button type="button" style={quiet} onClick={onDismiss}>I'll explore myself</button>
            </>
          )}

          {/* The fail-open door. Only ever rendered when a save has ALREADY
              failed, and it records NOTHING — no row, no skip flag — so the
              wizard is offered again next time. A customer must never be trapped
              behind our setup screen. */}
          {error && step !== STEP_DONE && (
            <button type="button" style={quiet} onClick={onDismiss}>Skip for now and go to the app</button>
          )}
        </div>
      </div>
    </div>
  );
}
